/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from 'react';

import { SurveyTemplateVersionRemoveCommand } from '@dreryk/egabinet-survey-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { Button, ButtonVariant, FormLeavingEventInterceptor } from '@dreryk/react-components';

import { generalSettingsTranslationKeys } from '../../../../translations';

import {
   useSurveyTemplateVersionCreateCommand,
   useSurveyTemplateVersionPublishCommand,
   useSurveyTemplateVersionUpdateCommand,
} from './SurveyPreInterviewTemplateForm.hooks';
import {
   SurveyPreInterviewTemplateFormBean,
   SurveyPreInterviewTemplateFormController,
} from './SurveyPreInterviewTemplateForm.types';

type SurveyPreInterviewTemplateFormEditButtonProps = {
   readonly onEditClick: VoidFunction;
   readonly variant: ButtonVariant;
};

export const SurveyPreInterviewTemplateFormEditButton = (
   props: SurveyPreInterviewTemplateFormEditButtonProps
): JSX.Element => {
   return (
      <Button
         compact
         label={generalSettingsTranslationKeys.survey.preInterview.form.actions.edit}
         testId={'edit'}
         variant={props.variant}
         onClick={props.onEditClick}
         type={'button'}
      />
   );
};

type SurveyPreInterviewTemplateFormDiscardChangesButtonProps = {
   readonly onSuccess: VoidFunction;
   readonly preInterviewTemplateId: number;
};

export const SurveyPreInterviewTemplateFormDiscardChangesButton = (
   props: SurveyPreInterviewTemplateFormDiscardChangesButtonProps
): JSX.Element => {
   const { executeAsync: removeTemplateVersion } = useCqrsCommand<void, unknown>(
      () => {
         const command = new SurveyTemplateVersionRemoveCommand();
         command.id = props.preInterviewTemplateId;
         return command;
      },
      { onSuccess: props.onSuccess }
   );

   return (
      <FormLeavingEventInterceptor
         active
         message={generalSettingsTranslationKeys.survey.preInterview.form.discardChangesPrompt.message}
         testId={'discardChangesConfirmationModal'}
         title={generalSettingsTranslationKeys.survey.preInterview.form.discardChangesPrompt.title}
         confirmationButtonLabel={
            generalSettingsTranslationKeys.survey.preInterview.form.discardChangesPrompt.actions.confirm
         }
      >
         <Button
            compact
            label={generalSettingsTranslationKeys.survey.preInterview.form.actions.discardChanges}
            testId={'discardChanges'}
            onClick={removeTemplateVersion}
         />
      </FormLeavingEventInterceptor>
   );
};

type SurveyPreInterviewTemplateFormPublishButtonProps = {
   readonly onSuccess: VoidFunction;
   readonly preInterviewTemplateId: number;
};

export const SurveyPreInterviewTemplateFormPublishButton = (
   props: SurveyPreInterviewTemplateFormPublishButtonProps
): JSX.Element => {
   const { executeAsync: publishTemplateVersion } = useSurveyTemplateVersionPublishCommand({
      onSuccess: props.onSuccess,
   });

   return (
      <Button
         compact
         label={generalSettingsTranslationKeys.survey.preInterview.form.actions.publish}
         testId={'publish'}
         variant={ButtonVariant.PRIMARY}
         onClick={async () => publishTemplateVersion(props.preInterviewTemplateId)}
         type={'button'}
      />
   );
};

type SurveyPreInterviewTemplateFormCancelButtonProps = Pick<SurveyPreInterviewTemplateFormController, 'reset'> & {
   readonly onCancel: VoidFunction;
};

export const SurveyPreInterviewTemplateFormCancelButton = (
   props: SurveyPreInterviewTemplateFormCancelButtonProps
): JSX.Element => {
   const handleCancel = useCallback(() => {
      props.onCancel();
      props.reset();
   }, [props.onCancel, props.reset]);

   return (
      <Button
         compact
         label={generalSettingsTranslationKeys.survey.preInterview.form.actions.cancel}
         testId={'cancel'}
         onClick={handleCancel}
         type={'button'}
      />
   );
};

type SurveyPreInterviewTemplateFormSaveButtonProps = Pick<SurveyPreInterviewTemplateFormController, 'handleSubmit'> & {
   readonly onSuccess: VoidFunction;
};

export const SurveyPreInterviewTemplateFormSaveButton = (
   props: SurveyPreInterviewTemplateFormSaveButtonProps
): JSX.Element => {
   const { executeAsync: createTemplateVersion } = useSurveyTemplateVersionCreateCommand();
   const { executeAsync: updateTemplateVersion } = useSurveyTemplateVersionUpdateCommand({
      onSuccess: props.onSuccess,
   });

   const savePreInterviewTemplate = useCallback(async (formData: SurveyPreInterviewTemplateFormBean) => {
      const newPreInterviewTemplateId = await createTemplateVersion();
      return updateTemplateVersion({ formData: formData, preInterviewTemplateId: newPreInterviewTemplateId });
   }, []);

   return (
      <Button
         compact
         label={generalSettingsTranslationKeys.survey.preInterview.form.actions.save}
         testId={'save'}
         onClick={props.handleSubmit(savePreInterviewTemplate)}
         type={'button'}
      />
   );
};

type SurveyPreInterviewTemplateFormSaveAndPublishButtonProps = Pick<
   SurveyPreInterviewTemplateFormController,
   'handleSubmit'
> & {
   readonly onSuccess: VoidFunction;
};

export const SurveyPreInterviewTemplateFormSaveAndPublishButton = (
   props: SurveyPreInterviewTemplateFormSaveAndPublishButtonProps
): JSX.Element => {
   const { executeAsync: createTemplateVersion } = useSurveyTemplateVersionCreateCommand();
   const { executeAsync: updateTemplateVersion } = useSurveyTemplateVersionUpdateCommand({});
   const { executeAsync: publishTemplateVersion } = useSurveyTemplateVersionPublishCommand({
      onSuccess: props.onSuccess,
   });

   const saveAndPublishPreInterviewTemplate = useCallback(
      async (formData: SurveyPreInterviewTemplateFormBean) => {
         const newPreInterviewTemplateId = await createTemplateVersion();
         await updateTemplateVersion({ formData: formData, preInterviewTemplateId: newPreInterviewTemplateId });
         await publishTemplateVersion(newPreInterviewTemplateId);
      },
      [createTemplateVersion, updateTemplateVersion, publishTemplateVersion]
   );

   return (
      <Button
         compact
         label={generalSettingsTranslationKeys.survey.preInterview.form.actions.saveAndPublish}
         testId={'saveAndPublish'}
         variant={ButtonVariant.PRIMARY}
         type={'button'}
         onClick={props.handleSubmit(saveAndPublishPreInterviewTemplate)}
      />
   );
};
